import z, {TypeOf} from "zod/v4";
import {Path, useForm, UseFormProps} from "react-hook-form";
import type {ComponentProps, JSX, PropsWithoutRef, ReactNode, RefObject} from "react";
import type {SubmitButton} from "./SubmitButton";


export type ZodObjectType = z.ZodObject

export type ErrorResult<S extends ZodObjectType> = {
  error: {
    message: string,
    path?: string | Path<TypeOf<S>>
  }
}
export type SuccessResult<R> = {
  success: Awaited<R>
}
export type OnSubmitResult<S extends ZodObjectType, R> = ErrorResult<S> | SuccessResult<R>
export const isErrorResult = <S extends ZodObjectType, R>(result: OnSubmitResult<S, R>): result is ErrorResult<S> => {
  return result !== undefined && 'error' in result;
}
export const isSuccessResult = <S extends ZodObjectType, R>(result: OnSubmitResult<S, R>): result is SuccessResult<R> => {
  return result !== undefined && 'success' in result;
}

export type FormRef<S extends ZodObjectType, O extends OnSubmit<S>> = {
  form?: Omit<ReturnType<typeof useForm<z.infer<S>>>, "handleSubmit"> & {
    handleSubmit: (onSubmit: O) => ComponentProps<'form'>['onSubmit']
  }
  formEl?: HTMLFormElement | null
}
export type OnSubmit<S extends ZodObjectType> = (values: z.infer<S>) => Promise<OnSubmitResult<S, unknown>>

export interface FormProps<S extends ZodObjectType, O extends OnSubmit<S>> extends Omit<PropsWithoutRef<JSX.IntrinsicElements["form"]>, "onSubmit"> {
  /** All your form fields */
  children?: ReactNode
  /** Text to display in the submit button */
  submitButtonProps?: ComponentProps<typeof SubmitButton>
  submitText?: string | false
  schema?: S
  onSubmit: O
  defaultValues?: UseFormProps<z.infer<S>>["defaultValues"],
  ref?: RefObject<FormRef<S, O> | undefined>
}
